import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Star, Send, CheckCircle2, MessageCircle } from "lucide-react";

const categories = [
  { v: "general", l: "General feedback" },
  { v: "bug", l: "Bug report" },
  { v: "feature", l: "Feature request" },
  { v: "praise", l: "Kind words" },
];

export default function FeedbackModal({ open, onOpenChange }) {
  const [message, setMessage] = useState("");
  const [category, setCategory] = useState("general");
  const [rating, setRating] = useState(0);
  const [contact, setContact] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const reset = () => {
    setMessage("");
    setCategory("general");
    setRating(0);
    setContact("");
    setDone(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!message.trim()) return;
    setBusy(true);
    try {
      await api.post("/feedback", {
        message: message.trim(),
        category,
        rating: rating || null,
        contact: contact.trim() || null,
      });
      setDone(true);
      toast.success("Thanks — every note makes ExamBro sharper.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(v) => {
        onOpenChange(v);
        if (!v) setTimeout(reset, 200);
      }}
    >
      <DialogContent className="sm:max-w-md" data-testid="feedback-modal">
        {done ? (
          <div className="text-center py-8">
            <CheckCircle2 className="h-12 w-12 mx-auto text-emerald-600 mb-3" />
            <DialogTitle className="font-heading text-2xl font-bold tracking-tight">Got it</DialogTitle>
            <p className="text-sm text-stone-500 mt-2 max-w-xs mx-auto">
              We read every message. If you left contact info, expect a reply.
            </p>
            <Button
              variant="outline"
              className="mt-6"
              onClick={() => {
                onOpenChange(false);
                setTimeout(reset, 200);
              }}
            >
              Close
            </Button>
          </div>
        ) : (
          <>
            <DialogHeader>
              <div className="flex items-center gap-2 mb-1">
                <MessageCircle className="h-4 w-4 text-orange-600" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                  Feedback
                </span>
              </div>
              <DialogTitle className="font-heading text-2xl font-bold tracking-tight">
                Help shape ExamBro
              </DialogTitle>
              <DialogDescription className="text-stone-600">
                Bug? Feature idea? Something we broke? Tell us — this goes straight to the team.
              </DialogDescription>
            </DialogHeader>

            <form onSubmit={submit} className="space-y-4">
              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Type</Label>
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="mt-1" data-testid="feedback-category">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {categories.map((c) => (
                      <SelectItem key={c.v} value={c.v}>
                        {c.l}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">Rating</Label>
                <div className="flex gap-1 mt-1" data-testid="feedback-rating">
                  {[1, 2, 3, 4, 5].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => setRating(n)}
                      className="p-1 hover:scale-110"
                      style={{ transition: "transform 0.15s ease" }}
                      data-testid={`rating-${n}`}
                    >
                      <Star
                        className={`h-6 w-6 ${
                          n <= rating ? "text-orange-500 fill-orange-500" : "text-stone-300"
                        }`}
                      />
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                  Your message
                </Label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={5}
                  required
                  placeholder="What's on your mind?"
                  className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                  data-testid="feedback-message"
                />
                <div className="text-[10px] font-mono text-stone-400 mt-1 text-right tabular-nums">
                  {message.length} / 2000
                </div>
              </div>

              <div>
                <Label className="font-mono text-[10px] uppercase tracking-[0.2em] text-stone-500">
                  Contact for reply (optional)
                </Label>
                <input
                  type="text"
                  value={contact}
                  onChange={(e) => setContact(e.target.value)}
                  placeholder="email, Twitter handle, anything"
                  className="mt-1 w-full rounded-md border border-stone-300 bg-white px-3 py-2 text-sm focus:border-orange-500 focus:outline-none focus:ring-2 focus:ring-orange-500/20"
                  data-testid="feedback-contact"
                />
                <p className="text-[10px] text-stone-400 mt-1">
                  Leave contact info if you want a personal reply from the team.
                </p>
              </div>

              <Button
                type="submit"
                disabled={busy || !message.trim()}
                className="w-full bg-orange-600 hover:bg-orange-700 text-white h-11"
                data-testid="feedback-submit"
              >
                <Send className="h-4 w-4 mr-2" />
                Send feedback
              </Button>
            </form>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
